import { useCallback, useState } from 'react';

import {
  trackButtonClick,
  trackError,
  trackProjectPageAction,
} from '../services/telemetry';

/** Format toggle that propagates to the backend's ``?format=`` query.
 *
 * `standard` and `sibelius` include fingerings. `plain` is the raw OMR
 * MusicXML with no fingering merge (PDF/image projects only).
 */
export type ProjectXmlDownloadFormat = 'standard' | 'sibelius' | 'plain';

/** Page-specific "go do the download" callback.
 *
 * The XML-flow page builds a SAS URL and ``window.open``s it; the
 * PDF-flow page streams the bytes via ``downloadProjectXml`` and
 * dispatches an ``<a download>`` click. The hook itself doesn't care
 * which is happening -- it just needs the awaitable.
 *
 * Implementations should throw on failure (network error, 4xx) so the
 * hook can surface a unified error toast / telemetry event.
 */
export type ProjectXmlDownloader = (
  format: ProjectXmlDownloadFormat
) => Promise<void>;

interface UseProjectXmlDownloadOptions {
  /** Telemetry ``source`` tag for ``trackButtonClick`` / ``trackError``.
   *  Should match the calling page (``'ProjectPage'`` or
   *  ``'PdfProjectPage'``) so the funnel stays readable. */
  source: string;
  /** Optional project id forwarded into ``trackError``'s payload.
   *  ``undefined`` is fine -- the hook simply omits it from the event
   *  properties in that case. */
  projectId?: string;
  /** Page-specific download implementation. See ``ProjectXmlDownloader``. */
  downloader: ProjectXmlDownloader;
  /** Async alert presenter (same shape as ``useDialog().showAlert``).
   *  Called when ``downloader`` throws. */
  showAlert: (title: string, message: string) => Promise<void>;
  /** Optional side-effect run after a successful download. Used to
   *  arm the project-page feedback prompt on a completed value
   *  moment rather than the click. */
  onDownloadSuccess?: () => void;
  extras?: { [key: string]: unknown };
}

interface UseProjectXmlDownloadReturn {
  handleDownloadXml: () => Promise<void>;
  handleDownloadXmlSibelius: () => Promise<void>;
  handleDownloadXmlPlain: () => Promise<void>;
  isDownloadingXml: boolean;
  isDownloadingXmlSibelius: boolean;
  isDownloadingXmlPlain: boolean;
}

const FORMAT_META: Record<
  ProjectXmlDownloadFormat,
  { click: string; action: string; errorAction: string; failMessage: string }
> = {
  standard: {
    click: 'Download XML',
    action: 'download_xml',
    errorAction: 'downloadXml',
    failMessage: 'Could not download the MusicXML file. Please try again.',
  },
  sibelius: {
    click: 'Download XML (Sibelius)',
    action: 'download_xml_sibelius',
    errorAction: 'downloadXmlSibelius',
    failMessage:
      'Could not download the Sibelius-compatible MusicXML. Please try again.',
  },
  plain: {
    click: 'Download XML (without fingerings)',
    action: 'download_xml_plain',
    errorAction: 'downloadXmlPlain',
    failMessage:
      'Could not download the MusicXML without fingerings. Please try again.',
  },
};

/**
 * Bundles the per-format busy flag, telemetry, and error-toast
 * boilerplate for MusicXML download flavors. Callers supply a
 * ``downloader`` (the page-specific fetch + save logic) and consume
 * the returned handlers directly on the toolbar.
 */
export const useProjectXmlDownload = ({
  source,
  projectId,
  downloader,
  showAlert,
  onDownloadSuccess,
  extras,
}: UseProjectXmlDownloadOptions): UseProjectXmlDownloadReturn => {
  const [isDownloadingXml, setIsDownloadingXml] = useState(false);
  const [isDownloadingXmlSibelius, setIsDownloadingXmlSibelius] =
    useState(false);
  const [isDownloadingXmlPlain, setIsDownloadingXmlPlain] = useState(false);

  const setterFor = (format: ProjectXmlDownloadFormat) => {
    if (format === 'sibelius') return setIsDownloadingXmlSibelius;
    if (format === 'plain') return setIsDownloadingXmlPlain;
    return setIsDownloadingXml;
  };

  const runDownload = useCallback(
    async (format: ProjectXmlDownloadFormat) => {
      const meta = FORMAT_META[format];
      const setBusy = setterFor(format);

      trackButtonClick(source, meta.click, extras);
      trackProjectPageAction(
        meta.action,
        source === 'PdfProjectPage' ? 'pdf' : 'xml',
        { source, ...(projectId ? { projectId } : {}), ...extras }
      );
      setBusy(true);
      try {
        await downloader(format);
        onDownloadSuccess?.();
      } catch (err) {
        console.error('Failed to download XML:', err);
        const errorObj = err instanceof Error ? err : new Error(String(err));
        trackError(errorObj, {
          source,
          action: meta.errorAction,
          ...(projectId ? { projectId } : {}),
          ...extras,
        });
        await showAlert('Download Failed', meta.failMessage);
      } finally {
        setBusy(false);
      }
    },
    [source, projectId, downloader, showAlert, onDownloadSuccess, extras]
  );

  const handleDownloadXml = useCallback(
    () => runDownload('standard'),
    [runDownload]
  );
  const handleDownloadXmlSibelius = useCallback(
    () => runDownload('sibelius'),
    [runDownload]
  );
  const handleDownloadXmlPlain = useCallback(
    () => runDownload('plain'),
    [runDownload]
  );

  return {
    handleDownloadXml,
    handleDownloadXmlSibelius,
    handleDownloadXmlPlain,
    isDownloadingXml,
    isDownloadingXmlSibelius,
    isDownloadingXmlPlain,
  };
};
